/**
 * Stable Firestore booking document IDs so the same extracted email/booking
 * upserts instead of creating a new booking + duplicate itinerary rows on re-sync,
 * reconnecting providers, or re-import after trip delete/recreate.
 */

/** Must match TripFlow/functions/src/utils/emailBookingDeterministicDocId.ts */
function sanitizeDocSegment(s: string, maxLen: number): string {
  return s.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, maxLen);
}

function simpleHash16(input: string): string {
  let h1 = 5381;
  let h2 = 52711;
  for (let i = 0; i < input.length; i++) {
    h1 = ((h1 << 5) + h1) ^ input.charCodeAt(i);
    h2 = ((h2 << 5) + h2) ^ (input.charCodeAt(i) * 131);
  }
  return `${(h1 >>> 0).toString(16).padStart(8, '0')}${(h2 >>> 0).toString(16).padStart(8, '0')}`.slice(0, 32);
}

function coerceTimeMs(v: unknown): number | null {
  if (v == null || v === undefined) return null;
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? null : v.getTime();
  }
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  if (
    typeof v === 'object' &&
    v !== null &&
    'toDate' in v &&
    typeof (v as { toDate?: () => Date }).toDate === 'function'
  ) {
    const d = (v as { toDate: () => Date }).toDate();
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  return null;
}

/** Earliest real travel-ish date among standard booking fields (YY-MM-DD in UTC-ish). */
function primaryTravelDateYmd(booking: Record<string, unknown>): string | null {
  const keys = ['departureDate', 'arrivalDate', 'checkInDate', 'checkOutDate'] as const;
  const times = keys.map((k) => coerceTimeMs(booking[k])).filter((t): t is number => t != null);
  if (!times.length) return null;
  times.sort((a, b) => a - b);
  return new Date(times[0]).toISOString().slice(0, 10);
}

function normalizeConfirmation(c: unknown): string | null {
  if (typeof c !== 'string' || !c.trim()) return null;
  const n = c.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  return n.length >= 4 ? n.slice(0, 32) : null;
}

/**
 * Returns a deterministic `trips/{tripId}/bookings/{id}` document ID.
 */
export function emailBookingDeterministicDocId(booking: Record<string, unknown>): string {
  const conf = normalizeConfirmation(booking.confirmationNumber);
  const ymd = primaryTravelDateYmd(booking);

  if (conf && ymd) {
    return `b_em_${conf}_${ymd}`;
  }

  const subject = typeof booking.rawEmailSubject === 'string' ? booking.rawEmailSubject.slice(0, 120) : '';
  const location = typeof booking.location === 'string' ? booking.location.slice(0, 80) : '';
  const provider = typeof booking.provider === 'string' ? booking.provider : '';
  const type = typeof booking.type === 'string' ? booking.type : '';
  const h = sanitizeDocSegment(simpleHash16(`${provider}|${type}|${ymd ?? 'nodate'}|${location}|${subject}`), 48);
  return `b_em_nf_${h}`;
}
